import React, { useEffect, useState } from 'react';
import { Github } from 'lucide-react';

interface GitHubStarButtonProps {
  repo: string;
  className?: string;
}

const formatStarCount = (count: number | null) => {
  if (count === null) return '?';

  return new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(count);
};

const GitHubStarButton: React.FC<GitHubStarButtonProps> = ({ repo, className = '' }) => {
  const [starCount, setStarCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStarCount = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`https://api.github.com/repos/${repo}`);
        if (response.ok) {
          const data = await response.json();
          setStarCount(data.stargazers_count);
        } else {
          console.error('Failed to fetch GitHub stars');
        }
      } catch (error) {
        console.error('Error fetching GitHub stars:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStarCount();
  }, [repo]);

  return (
    <a
      href={`https://github.com/${repo}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex h-10 items-center gap-2 rounded-full border border-[var(--vi-border-primary)] bg-[color-mix(in_srgb,var(--vi-bg-card)_88%,transparent)] px-3 text-[13px] font-extrabold text-[var(--vi-text-secondary)] shadow-[0_1px_2px_var(--vi-shadow-sm)] transition-colors duration-200 hover:border-[var(--vi-border-strong)] hover:bg-[var(--vi-bg-card)] hover:text-[var(--vi-text-primary)] ${className}`}
      title="Star on GitHub"
      aria-label={`Star ${repo} on GitHub`}
    >
      <Github className="h-4 w-4 shrink-0" />
      <span className="leading-none">Star</span>
      <span className="inline-flex min-w-[34px] items-center justify-center rounded-full bg-[var(--vi-fill-highlight-16)] px-2 py-1 text-[11px] font-extrabold leading-none text-[var(--vi-accent-highlight-strong)]">
        {isLoading ? (
          <span className="h-1.5 w-5 rounded-full bg-[var(--vi-fill-highlight-28)]" />
        ) : (
          formatStarCount(starCount)
        )}
      </span>
    </a>
  );
};

export default GitHubStarButton;
